import { useState } from 'react';
import { Table, Badge, Button, Input, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Globe, RefreshCw } from 'lucide-react';
import { SectionCard, QueryState, StatusBadge, KeyValue } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import Can from '@rbac/Can';

/**
 * SELL-08 Domains (real since D7, D16): this store's hostnames from the
 * platform registry. A custom domain starts pending; it goes live once the
 * TXT record below is found ("Check DNS").
 */
export default function DomainsTab({ store }) {
  const { toast } = useToast();
  const q = useQuery('merchantDomains', {}, { select: (r) => r.data });
  const add = useMutation('merchantDomainAdd');
  const verify = useMutation('merchantDomainVerify');
  const remove = useMutation('merchantDomainRemove');
  const [domain, setDomain] = useState('');
  const [checks, setChecks] = useState({}); // id → message from the last check
  const pending = (q.data || []).filter((d) => d.dns);

  const doAdd = async () => {
    try {
      const res = await add.run({ domain });
      toast.success('Domain added', `${res.data.domain}: set the DNS records below.`);
      setDomain('');
      q.reload();
    } catch { /* add.error */ }
  };
  const doVerify = async (d) => {
    try {
      const res = await verify.run({ id: d.id });
      if (res.data.verified) {
        toast.success('Domain verified', d.domain);
        q.reload();
      } else setChecks((c) => ({ ...c, [d.id]: res.data.message }));
    } catch { /* verify.error */ }
  };
  const doRemove = async (d) => {
    try {
      await remove.run({ id: d.id });
      toast.success('Domain removed', d.domain);
      q.reload();
    } catch { /* remove.error */ }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="This store">
        <KeyValue columns={2} items={[
          { label: 'Business name', value: store && store.name },
          { label: 'Store slug', value: store && <span className="font-mono">{store.slug}</span> },
          { label: 'Admin address', value: typeof window !== 'undefined' ? `${window.location.host}/admin` : '' },
        ]} />
      </SectionCard>
      <SectionCard title="Domains">
        <QueryState query={q} skeleton="table">
          {(rows) => (
            <Table
              data={rows}
              columns={[
                { key: 'domain', label: 'Domain', render: (d) => <span className="font-medium">{d.domain} {d.primary ? <Badge variant="primary" size="sm">Primary</Badge> : null}</span> },
                { key: 'kind', label: 'Type', hideOnMobile: true },
                { key: 'status', label: 'Verification', render: (d) => <StatusBadge status={d.status} /> },
                { key: 'ssl', label: 'SSL', render: (d) => <StatusBadge status={d.ssl === 'active' ? 'active' : 'pending'} label={d.ssl} /> },
              ]}
              showSerial={false}
              withAction={false}
              showToolbar={false}
            />
          )}
        </QueryState>
        <Can module="store" action="domains">
          <div className="grid gap-2 border-t border-[var(--app-border)] pt-3 sm:grid-cols-[1fr_auto] items-start">
            <Input
              label="Add a domain you own"
              placeholder="shop.yourstore.in"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              leftIcon={<Globe size={16} />} />
            <div className="sm:pt-6"><Button onClick={doAdd} loading={add.loading} disabled={!domain.trim()}>Add domain</Button></div>
          </div>
          {add.error ? <Alert variant="error">{add.error}</Alert> : null}
        </Can>
      </SectionCard>
      {pending.map((d) => (
        <SectionCard key={d.id} title={`Verify ${d.domain}`}>
          <p className="text-sm">Add these DNS records at your registrar. The TXT record proves the domain is yours; the CNAME points it at your store.</p>
          {d.dns.records.map((r) => (
            <KeyValue key={r.type} columns={3} items={[
              { label: 'Type', value: r.type },
              { label: 'Host', value: <span className="font-mono break-all">{r.host}</span> },
              { label: 'Value', value: <span className="font-mono break-all">{r.value}</span> },
            ]} />
          ))}
          {checks[d.id] ? <Alert variant="warning">{checks[d.id]}</Alert> : null}
          {verify.error ? <Alert variant="error">{verify.error}</Alert> : null}
          <Can module="store" action="domains">
            <div className="flex flex-wrap gap-2">
              <Button leftIcon={<RefreshCw size={16} />} onClick={() => doVerify(d)} loading={verify.loading}>Check DNS</Button>
              <Button variant="ghost" onClick={() => doRemove(d)} loading={remove.loading}>Remove</Button>
            </div>
          </Can>
        </SectionCard>
      ))}
    </div>
  );
}
